/**
 * `usePageTransition` — the page arriving (#2172, the laptop motion pass;
 * first built on the 4 Oct UI-motion branch).
 *
 * On a PATHNAME change the route content fades in and rises 6 px over
 * 220 ms, easing out like the rest of the SPA (D4: calm, no overshoot).
 * Search-param-only changes (the roster's Earlier / Later, a `?surface=`
 * panel) never play it: the effect keys on the pathname alone.
 *
 * Nothing is remounted. The hook animates the live element with `motion`
 * (`transform` and `opacity` only), so the outgoing page is simply
 * replaced and the incoming one arrives; once it lands the page carries no
 * transform and no opacity below 1, so no stacking context.
 *
 * Two guards keep the rest of the page still:
 *   - **Fixed descendants.** A transform on an ancestor turns it into the
 *     containing block of every `position: fixed` descendant (the phone
 *     roster's bottom bar, a pinned action row), which would make them
 *     jump. The rise only runs when the content holds no fixed element,
 *     and it is cancelled the moment one mounts mid-way; the fade (opacity
 *     does not re-anchor fixed elements) always runs.
 *   - **Nested layouts.** A layout that keeps its own chrome between its
 *     pages (Setup's sidebar) marks its outlet `data-route-outlet`. When
 *     the previous page's outlet is still on the page after the change,
 *     only that outlet animates, so the sidebar stays put and its active
 *     highlight glides instead of fading in with the page.
 *
 * It does nothing on the first mount (a page load), without
 * `element.animate`, under a test runner, and under
 * `prefers-reduced-motion` (`jsMotionEnabled`): the page is simply there.
 */
import { animate } from "motion/mini";
import { type RefObject, useLayoutEffect, useRef } from "react";

import { EASE_OUT_SOFT_POINTS, jsMotionEnabled, seconds } from "./motionTokens";

export const PAGE_TRANSITION_MS = 220;
export const PAGE_TRANSITION_RISE_PX = 6;

/** A nested layout's outlet — animated instead of the whole page when the
 *  layout itself survives the navigation. */
export const ROUTE_OUTLET_SELECTOR = "[data-route-outlet]";

/** Tailwind's `fixed` (plain or behind a variant) and inline fixed styles. */
const FIXED_SELECTOR = '.fixed, [class*=":fixed"], [style*="position: fixed"]';

/** Whether `el` is, or contains, an element positioned `fixed` by class or
 *  inline style — the cheap check that decides whether the rise may run. */
export function holdsFixedElement(el: Element): boolean {
  return el.matches(FIXED_SELECTOR) || el.querySelector(FIXED_SELECTOR) !== null;
}

/** Whether JS-driven motion may run on `el` (reduced motion, tests, no WAAPI). */
function canAnimate(el: HTMLElement): boolean {
  return jsMotionEnabled() && typeof el.animate === "function";
}

interface RunningTransition {
  target: HTMLElement;
  finished: () => boolean;
  cancel: () => void;
}

export function usePageTransition(rootRef: RefObject<HTMLElement | null>, pathname: string): void {
  // The pathname the last run saw. A re-run for the same pathname (a
  // Suspense boundary re-showing the page, StrictMode's double effect)
  // plays nothing; only a real move to another pathname does.
  const lastPathRef = useRef<string | null>(null);
  // The nested-layout outlet on screen after the last navigation.
  const outletRef = useRef<HTMLElement | null>(null);
  // The running transition, so a quick second navigation cancels the first.
  const runningRef = useRef<RunningTransition | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the pathname IS the trigger.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const previousPath = lastPathRef.current;
    lastPathRef.current = pathname;
    const previousOutlet = outletRef.current;
    outletRef.current = root.querySelector<HTMLElement>(ROUTE_OUTLET_SELECTOR);
    // First mount (a page load) or the same page again: nothing to play.
    if (previousPath === null || previousPath === pathname) return;
    if (!canAnimate(root)) return;

    const target =
      previousOutlet?.isConnected && root.contains(previousOutlet) ? previousOutlet : root;
    // A redirect right after a navigation (Setup → its first page) lands
    // inside the transition already playing: let that one finish rather
    // than restart the fade on top of it.
    const running = runningRef.current;
    if (running && !running.finished() && running.target.contains(target)) return;
    running?.cancel();

    const options = { duration: seconds(PAGE_TRANSITION_MS), ease: EASE_OUT_SOFT_POINTS };
    let done = false;
    let observer: MutationObserver | null = null;
    const fade = animate(target, { opacity: [0, 1] }, options);
    let rise: ReturnType<typeof animate> | null = null;
    if (!holdsFixedElement(target)) {
      rise = animate(
        target,
        { transform: [`translateY(${PAGE_TRANSITION_RISE_PX}px)`, "none"] },
        options,
      );
      // A fixed element mounting mid-rise (data arriving) drops the rise.
      if (typeof MutationObserver === "function") {
        observer = new MutationObserver((records) => {
          for (const record of records) {
            for (const node of Array.from(record.addedNodes)) {
              if (node instanceof Element && holdsFixedElement(node)) {
                rise?.cancel();
                observer?.disconnect();
                return;
              }
            }
          }
        });
        observer.observe(target, { childList: true, subtree: true });
      }
    }
    fade.finished.then(() => {
      done = true;
      observer?.disconnect();
    });
    runningRef.current = {
      target,
      finished: () => done,
      cancel: () => {
        done = true;
        fade.cancel();
        rise?.cancel();
        observer?.disconnect();
        // Never leave the page half-faded.
        target.style.opacity = "";
        target.style.transform = "";
      },
    };
  }, [pathname]);

  // Unmount: drop a transition still running.
  useLayoutEffect(() => () => runningRef.current?.cancel(), []);
}
